import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X, LogOut, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AdminNav from '../components/AdminNav';
import { policyService, authService } from '../services/api';

function AdminPolicies() {
  const navigate = useNavigate();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '', icon: '✓' });

  useEffect(() => {
    const fetchPolicies = async () => {
      try {
        const response = await policyService.getAll();
        if (response.data) {
          setPolicies(response.data);
        }
      } catch (err) {
        setError('خطا در دریافت سیاست‌ها');
      } finally {
        setLoading(false);
      }
    };
    fetchPolicies();
  }, []);

  const handleLogout = () => {
    authService.logout();
    navigate('/admin-login');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let updated;
      if (editingId) {
        const response = await policyService.update(editingId, formData);
        updated = policies.map(p => p.id === editingId ? response.data : p);
      } else {
        const response = await policyService.create(formData);
        updated = [...policies, response.data];
      }
      setPolicies(updated);
      setShowForm(false);
      setEditingId(null);
      setFormData({ title: '', description: '', icon: '✓' });
      setError('');
    } catch (err) {
      setError('خطا در ذخیره سیاست');
    }
  };

  const handleEdit = (policy) => {
    setFormData({ title: policy.title, description: policy.description, icon: policy.icon || '✓' });
    setEditingId(policy.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا مطمئن هستید؟')) return;
    try {
      await policyService.delete(id);
      setPolicies(policies.filter(p => p.id !== id));
    } catch (err) {
      setError('خطا در حذف سیاست');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ title: '', description: '', icon: '✓' });
  };

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">در حال بارگذاری...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold mb-2">مدیریت سیاست‌ها</h1>
              <p className="text-xl text-blue-100">ویرایش سیاست‌های نشریه</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => navigate('/admin/articles')} className="flex items-center gap-2 px-4 py-2 bg-blue-700 hover:bg-blue-600 rounded-lg font-semibold transition">
                <ArrowRight size={18} />
                بازگشت
              </button>
              <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition">
                <LogOut size={18} />
                خروج
              </button>
            </div>
          </div>
        </div>
      </div>

      <AdminNav />

      <div className="max-w-7xl mx-auto px-4 py-12">
        {error && <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">{error}</div>}

        {!showForm && (
          <button onClick={() => setShowForm(true)} className="mb-8 flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-bold hover:from-green-700 hover:to-green-800 transition shadow-lg">
            <Plus size={20} />
            افزودن سیاست جدید
          </button>
        )}

        {showForm && (
          <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-md p-8 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">{editingId ? 'ویرایش سیاست' : 'افزودن سیاست جدید'}</h2>
              <button type="button" onClick={handleCancel} className="p-2 hover:bg-gray-200 rounded-lg transition">
                <X size={24} className="text-gray-600" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">عنوان</label>
                <input type="text" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} required className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">توضیحات</label>
                <textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} required rows="4" className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">آیکون (Emoji)</label>
                <input type="text" value={formData.icon} onChange={e => setFormData({ ...formData, icon: e.target.value })} className="w-32 px-4 py-3 border border-gray-300 rounded-lg text-2xl" />
              </div>
              <div className="flex gap-4 pt-4">
                <button type="submit" className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition">ذخیره</button>
                <button type="button" onClick={handleCancel} className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-400 transition">انصراف</button>
              </div>
            </div>
          </form>
        )}

        <div className="space-y-6">
          {policies.length ? policies.map(policy => (
            <div key={policy.id} className="bg-white rounded-xl shadow-md p-8">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <span className="text-3xl">{policy.icon || '✓'}</span>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">{policy.title}</h2>
                    <p className="text-gray-700 leading-relaxed">{policy.description}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleEdit(policy)} className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"><Edit2 size={16} /></button>
                  <button onClick={() => handleDelete(policy.id)} className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"><Trash2 size={16} /></button>
                </div>
              </div>
            </div>
          )) : (
            <div className="bg-white rounded-xl shadow-md p-8 text-center text-gray-500">
              <p>هیچ سیاستی برای نمایش وجود ندارد</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminPolicies;
